import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { ContentUploadCard } from '@/features/releases/ContentUploadCard'
import { ReleaseRow } from '@/features/releases/ReleaseRow'
import { WebglBuildsCard } from '@/features/releases/WebglBuildsCard'
import { RELEASE_PLATFORMS, releasesQuery } from '@/features/releases/releases.queries'
import { Card, CardHeader, ErrorState, LoadingState, PageHeader } from '@/ui'

/**
 * The boot gate: which build each platform is on, and where its content lives.
 *
 * Everything here is gated on `release.manage`. Hiding the page is a courtesy — the server checks
 * the permission on every one of these routes.
 */
export const Route = createFileRoute('/_authed/releases')({
  component: ReleasesPage,
})

function ReleasesPage() {
  const { t } = useTranslation()
  const query = useQuery(releasesQuery)

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title={t('releases.title')} description={t('releases.description')} />

      <Card>
        <CardHeader title={t('releases.gateTitle')} description={t('releases.gateDescription')} />

        {query.isPending && <LoadingState />}

        {query.isError && (
          <ErrorState
            error={query.error}
            onRetry={() => {
              void query.refetch()
            }}
          />
        )}

        {query.isSuccess && (
          <div className="flex flex-col">
            {/*
             * Ordered by the page's own list rather than by whatever the API returned, so the
             * platforms keep a stable order and one added later lands at the end instead of
             * shuffling the rest.
             */}
            {RELEASE_PLATFORMS.map((platform) => {
              const release = query.data.find((row) => row.platform === platform)
              return release === undefined ? null : (
                <ReleaseRow key={platform} release={release} />
              )
            })}
          </div>
        )}
      </Card>

      <ContentUploadCard />

      <WebglBuildsCard />
    </div>
  )
}
